import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    ChartBar, Users, ShoppingCart, Coins, Path, Package,
    TrendUp, Star, ChatCircle, Lightning, ArrowLeft
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export default function AdminAnalytics() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}/admin/analytics`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : null)
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <Layout><div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
    </div></Layout>;

    if (!data) return <Layout><div className="max-w-4xl mx-auto px-4 py-6">
        <Card className="glass-card"><CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Admin access required</p>
        </CardContent></Card>
    </div></Layout>;

    const o = data.overview;
    const w = data.week;

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-cyber-purple text-glow-purple">PLATFORM</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="admin-analytics-title">
                        Admin Analytics
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">Bird's-eye view of Boltorium</p>
                </motion.div>

                {/* Overview Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                    {[
                        { label: 'Users', val: o.total_users, icon: Users, color: 'text-primary' },
                        { label: 'Orders', val: o.total_orders, icon: ShoppingCart, color: 'text-neon-gold' },
                        { label: 'Rides', val: o.total_rides, icon: Path, color: 'text-electric-blue' },
                        { label: 'Products', val: o.total_products, icon: Package, color: 'text-cyber-purple' },
                        { label: 'Sellers', val: o.total_sellers, icon: TrendUp, color: 'text-neon-green' },
                    ].map(s => (
                        <Card key={s.label} className="glass-card">
                            <CardContent className="p-3 text-center">
                                <s.icon size={18} className={`${s.color} mx-auto mb-1`} />
                                <p className={`text-xl font-black font-mono ${s.color}`}>{s.val}</p>
                                <p className="text-[9px] text-muted-foreground font-display">{s.label}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Revenue */}
                <div className="grid sm:grid-cols-3 gap-3 mb-6">
                    <Card className="glass-card border-neon-gold/20">
                        <CardContent className="p-4">
                            <p className="text-[10px] text-muted-foreground font-display">ORDER REVENUE</p>
                            <p className="text-2xl font-black font-mono text-neon-gold mt-1">{data.revenue.order_revenue_boltz?.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">Boltz</p>
                        </CardContent>
                    </Card>
                    <Card className="glass-card border-primary/20">
                        <CardContent className="p-4">
                            <p className="text-[10px] text-muted-foreground font-display">STRIPE REVENUE</p>
                            <p className="text-2xl font-black font-mono text-primary mt-1">{data.revenue.stripe_revenue_display}</p>
                            <p className="text-xs text-muted-foreground">CAD</p>
                        </CardContent>
                    </Card>
                    <Card className="glass-card border-cyber-purple/20">
                        <CardContent className="p-4">
                            <p className="text-[10px] text-muted-foreground font-display">BOLTZ IN CIRCULATION</p>
                            <p className="text-2xl font-black font-mono text-cyber-purple mt-1">{data.boltz_circulation?.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">Total supply held</p>
                        </CardContent>
                    </Card>
                </div>

                {/* User Tiers */}
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    <Card className="glass-card">
                        <CardContent className="p-4">
                            <h3 className="font-display text-xs text-muted-foreground tracking-wider mb-3">USER TIERS</h3>
                            <div className="space-y-2">
                                {[
                                    { label: 'Free', val: data.users.free, color: 'text-muted-foreground' },
                                    { label: 'Standard', val: data.users.standard, color: 'text-electric-blue' },
                                    { label: 'VIP', val: data.users.vip, color: 'text-neon-gold' },
                                ].map(t => (
                                    <div key={t.label} className="flex items-center justify-between">
                                        <span className={`text-sm ${t.color}`}>{t.label}</span>
                                        <span className="font-mono font-bold text-foreground">{t.val}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="glass-card">
                        <CardContent className="p-4">
                            <h3 className="font-display text-xs text-muted-foreground tracking-wider mb-3">THIS WEEK</h3>
                            <div className="space-y-2">
                                {[
                                    { label: 'New Users', val: w.new_users },
                                    { label: 'Orders', val: w.orders },
                                    { label: 'Rides', val: w.rides },
                                    { label: 'Posts', val: w.posts },
                                ].map(t => (
                                    <div key={t.label} className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">{t.label}</span>
                                        <span className="font-mono font-bold text-foreground">{t.val}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Engagement */}
                <Card className="glass-card mb-6">
                    <CardContent className="p-4">
                        <h3 className="font-display text-xs text-muted-foreground tracking-wider mb-3">ENGAGEMENT</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                                { label: 'Reviews', val: data.engagement.total_reviews, icon: Star },
                                { label: 'Posts', val: data.engagement.total_posts, icon: ChatCircle },
                                { label: 'Challenges', val: data.engagement.total_challenges, icon: Lightning },
                                { label: 'Referrals', val: data.engagement.total_referrals, icon: Users },
                            ].map(e => (
                                <div key={e.label} className="text-center p-2 rounded-lg bg-white/5">
                                    <e.icon size={16} className="text-muted-foreground mx-auto mb-1" />
                                    <p className="font-mono font-bold text-foreground">{e.val}</p>
                                    <p className="text-[9px] text-muted-foreground">{e.label}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Top Products */}
                {data.top_products?.length > 0 && (
                    <Card className="glass-card">
                        <CardContent className="p-4">
                            <h3 className="font-display text-xs text-muted-foreground tracking-wider mb-3">TOP PRODUCTS</h3>
                            <div className="space-y-2">
                                {data.top_products.map((p, i) => (
                                    <div key={p.item_id} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold text-primary font-mono w-5">#{i + 1}</span>
                                            <span className="text-sm text-foreground">{p.name || p.item_id}</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="font-mono text-neon-gold text-sm">{p.revenue?.toLocaleString()} Boltz</span>
                                            <span className="text-[10px] text-muted-foreground ml-2">({p.orders} orders)</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
